import { useState } from 'react';

type AnalysisType = 'image' | 'audio' | 'behavior' | 'spatial';

export default function TechnologySection() {
  const [activeAnalysis, setActiveAnalysis] = useState<AnalysisType>('behavior');

  const analysisTypes = [
    { 
      id: 'image' as AnalysisType, 
      label: '画像解析', 
      subLabel: 'Image Analysis',
      icon: 'ri-image-line', 
      color: '#00d4ff',
      description: '360°映像から空間情報、人の動き、医療機器の配置などを自動認識'
    },
    { 
      id: 'audio' as AnalysisType, 
      label: '音声解析', 
      subLabel: 'Audio Analysis',
      icon: 'ri-volume-up-line', 
      color: '#a855f7',
      description: '会話内容を文字起こしし、コミュニケーションパターンや専門用語の使用を解析'
    },
    { 
      id: 'behavior' as AnalysisType, 
      label: '行動・視線解析', 
      subLabel: 'Behavior & Gaze Analysis',
      icon: 'ri-eye-line', 
      color: '#ff6b35',
      description: 'ユーザーの視線移動、注視時間、頭部動作から注意配分や意思決定プロセスを可視化'
    },
    { 
      id: 'spatial' as AnalysisType, 
      label: '空間認識解析', 
      subLabel: 'Spatial Recognition Analysis',
      icon: 'ri-compass-3-line', 
      color: '#4ade80',
      description: '3D空間における位置関係、移動線、距離感覚を解析し、空間認知能力を評価'
    },
  ];

  const currentAnalysis = analysisTypes.find(a => a.id === activeAnalysis)!;

  return (
    <section className="relative w-full py-32 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800">
      <div className="container mx-auto px-8">
        {/* Title */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center px-4 py-2 bg-blue-600/20 backdrop-blur-sm rounded-full border border-blue-400/30 mb-6">
            <i className="ri-cpu-line text-cyan-400 mr-2"></i>
            <span className="text-sm font-medium text-white">Core Technology</span>
          </div>
          <h2 className="text-5xl lg:text-6xl font-black text-white mb-4" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">VRCHEL</span>
          </h2>
          <p className="text-2xl text-cyan-400 mb-4">世界初の360° AI解析システム</p>
          <p className="text-lg text-blue-100 max-w-3xl mx-auto" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
            VR映像とユーザー行動データの複合解析により、マニュアル化困難なスキルを可視化
          </p>
        </div>

        {/* Analysis Function Buttons */}
        <div className="max-w-6xl mx-auto mb-16">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {analysisTypes.map((analysis) => (
              <button
                key={analysis.id}
                onClick={() => setActiveAnalysis(analysis.id)}
                className={`relative p-6 rounded-2xl border-2 transition-all duration-500 cursor-pointer text-left ${
                  activeAnalysis === analysis.id
                    ? 'scale-105 shadow-2xl'
                    : 'hover:scale-102 border-white/20 bg-white/5'
                }`}
                style={{
                  borderColor: activeAnalysis === analysis.id ? analysis.color : undefined,
                  backgroundColor: activeAnalysis === analysis.id ? `${analysis.color}15` : undefined,
                  boxShadow: activeAnalysis === analysis.id ? `0 0 40px ${analysis.color}50` : undefined,
                }}
              >
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
                  style={{
                    backgroundColor: `${analysis.color}30`,
                    border: `2px solid ${analysis.color}`,
                  }}
                >
                  <i className={`${analysis.icon} text-3xl`} style={{ color: analysis.color }}></i>
                </div>
                <h3 className="text-lg font-bold text-white mb-1" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                  {analysis.label}
                </h3>
                <p className="text-xs text-gray-400">{analysis.subLabel}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Dynamic Demo Visualization */}
        <div className="max-w-7xl mx-auto">
          <div 
            className="relative rounded-3xl border-2 overflow-hidden bg-black shadow-2xl"
            style={{
              borderColor: currentAnalysis.color,
              boxShadow: `0 0 60px ${currentAnalysis.color}40`,
            }}
          >
            {/* Description Header */}
            <div 
              className="px-8 py-6 border-b-2"
              style={{
                backgroundColor: `${currentAnalysis.color}10`,
                borderColor: `${currentAnalysis.color}30`,
              }}
            >
              <div className="flex items-center gap-4">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{
                    backgroundColor: `${currentAnalysis.color}30`,
                    border: `2px solid ${currentAnalysis.color}`,
                  }}
                >
                  <i className={`${currentAnalysis.icon} text-2xl`} style={{ color: currentAnalysis.color }}></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                    {currentAnalysis.label}
                  </h3>
                  <p className="text-sm text-blue-100" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                    {currentAnalysis.description}
                  </p>
                </div>
              </div>
            </div>

            <div className="aspect-video relative">
              {/* Base Medical Scene Image */}
              <img
                src="https://jollygood.co.jp/vrimpact/ai-strategy/vrchel-analysis-base.jpg"
                alt="Medical VR Scene"
                className="w-full h-full object-cover object-top"
                onError={(e) => {
                  e.currentTarget.src = "https://readdy.ai/api/search-image?query=Japanese%20medical%20professionals%20in%20hospital%20operating%20room%20performing%20surgery%20with%20multiple%20staff%20members%2C%20realistic%20clinical%20environment%20with%20medical%20equipment%20and%20surgical%20team%20collaboration%2C%20overhead%20perspective%20showing%20full%20spatial%20context&width=1200&height=675&seq=vrchel-base-001&orientation=landscape";
                }}
              />

              {/* Image Analysis Overlay */}
              {activeAnalysis === 'image' && (
                <div className="absolute inset-0 pointer-events-none animate-fadeIn">
                  <svg className="w-full h-full" viewBox="0 0 1200 675">
                    {/* Object Detection Boxes */}
                    <g className="animate-pulse">
                      {/* Medical Equipment Detection */}
                      <rect x="150" y="200" width="180" height="220" fill="none" stroke="#00d4ff" strokeWidth="3" />
                      <rect x="150" y="180" width="100" height="20" fill="#00d4ff" />
                      <text x="155" y="195" fill="#000" fontSize="14" fontWeight="bold">医療機器</text>
                      
                      <rect x="500" y="250" width="200" height="180" fill="none" stroke="#00d4ff" strokeWidth="3" />
                      <rect x="500" y="230" width="80" height="20" fill="#00d4ff" />
                      <text x="505" y="245" fill="#000" fontSize="14" fontWeight="bold">患者</text>
                      
                      <rect x="850" y="180" width="160" height="240" fill="none" stroke="#00d4ff" strokeWidth="3" />
                      <rect x="850" y="160" width="100" height="20" fill="#00d4ff" />
                      <text x="855" y="175" fill="#000" fontSize="14" fontWeight="bold">医療スタッフ</text>
                      
                      {/* Spatial Grid */}
                      <g opacity="0.3">
                        {[...Array(8)].map((_, i) => (
                          <line key={`v${i}`} x1={i * 150} y1="0" x2={i * 150} y2="675" stroke="#00d4ff" strokeWidth="1" />
                        ))}
                        {[...Array(5)].map((_, i) => (
                          <line key={`h${i}`} x1="0" y1={i * 135} x2="1200" y2={i * 135} stroke="#00d4ff" strokeWidth="1" />
                        ))}
                      </g>
                    </g>
                  </svg>
                  
                  {/* Info Panel */}
                  <div className="absolute top-8 right-8 bg-black/95 backdrop-blur-sm border-2 border-[#00d4ff] rounded-xl p-6 max-w-sm">
                    <h4 className="text-lg font-bold text-[#00d4ff] mb-4 flex items-center gap-2" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                      <i className="ri-scan-line"></i>
                      リアルタイム物体検知
                    </h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-white" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>医療機器</span>
                        <span className="text-xs text-[#00d4ff] font-mono">98.5%</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-white" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>人物検知</span>
                        <span className="text-xs text-[#00d4ff] font-mono">99.2%</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-white" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>空間マッピング</span>
                        <span className="text-xs text-[#00d4ff] font-mono">97.8%</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Audio Analysis Overlay */}
              {activeAnalysis === 'audio' && (
                <div className="absolute inset-0 pointer-events-none animate-fadeIn">
                  {/* Audio Waveform Visualization */}
                  <div className="absolute bottom-8 left-8 right-8 bg-black/95 backdrop-blur-sm border-2 border-[#a855f7] rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <i className="ri-mic-line text-2xl text-[#a855f7]"></i>
                      <span className="text-lg text-white font-bold" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>音声解析中...</span>
                    </div>
                    <svg className="w-full h-24 mb-4" viewBox="0 0 1000 96">
                      {[...Array(100)].map((_, i) => (
                        <rect
                          key={i}
                          x={i * 10}
                          y={48 - Math.sin(i * 0.3) * 30 - Math.random() * 15}
                          width="8"
                          height={Math.abs(Math.sin(i * 0.3) * 60) + Math.random() * 30}
                          fill="#a855f7"
                          opacity={0.5 + Math.random() * 0.5}
                          className="animate-pulse"
                          style={{ animationDelay: `${i * 0.02}s` }}
                        />
                      ))}
                    </svg>
                    
                    {/* Transcription */}
                    <div className="space-y-2">
                      <div className="bg-[#a855f7]/20 rounded-lg p-3 border-l-4 border-[#a855f7]">
                        <p className="text-xs text-gray-400 mb-1">00:15 - 医師A</p>
                        <p className="text-sm text-white" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                          「バイタルサインを確認してください」
                        </p>
                      </div>
                      <div className="bg-[#a855f7]/20 rounded-lg p-3 border-l-4 border-[#a855f7]">
                        <p className="text-xs text-gray-400 mb-1">00:18 - 看護師B</p>
                        <p className="text-sm text-white" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                          「血圧120/80、心拍数72、正常範囲内です」
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Keyword Analysis */}
                  <div className="absolute top-8 right-8 bg-black/95 backdrop-blur-sm border-2 border-[#a855f7] rounded-xl p-6 max-w-xs">
                    <h4 className="text-lg font-bold text-[#a855f7] mb-4" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                      専門用語検出
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {['バイタルサイン', '血圧', '心拍数', '酸素飽和度', '体温', '呼吸数'].map((term) => (
                        <span 
                          key={term}
                          className="px-3 py-1 bg-[#a855f7]/30 border border-[#a855f7] rounded-full text-xs text-white"
                          style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                        >
                          {term}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Behavior & Gaze Analysis Overlay */}
              {activeAnalysis === 'behavior' && (
                <div className="absolute inset-0 pointer-events-none animate-fadeIn">
                  <svg className="w-full h-full" viewBox="0 0 1200 675">
                    {/* Gaze Tracking */}
                    <g className="animate-pulse">
                      {/* Gaze Points */}
                      <circle cx="400" cy="300" r="30" fill="#ff6b35" opacity="0.3" />
                      <circle cx="400" cy="300" r="15" fill="#ff6b35" opacity="0.6" />
                      <circle cx="400" cy="300" r="5" fill="#ff6b35" />
                      
                      <circle cx="600" cy="250" r="25" fill="#ffeb3b" opacity="0.3" />
                      <circle cx="600" cy="250" r="12" fill="#ffeb3b" opacity="0.6" />
                      <circle cx="600" cy="250" r="4" fill="#ffeb3b" />
                      
                      <circle cx="800" cy="350" r="35" fill="#ff6b35" opacity="0.3" />
                      <circle cx="800" cy="350" r="18" fill="#ff6b35" opacity="0.6" />
                      <circle cx="800" cy="350" r="6" fill="#ff6b35" />
                      
                      {/* Gaze Path */}
                      <path 
                        d="M 400 300 Q 500 270 600 250 T 800 350" 
                        stroke="#ffeb3b" 
                        strokeWidth="3" 
                        fill="none"
                        strokeDasharray="10,5"
                        markerEnd="url(#arrow-gaze)"
                      />
                      
                      {/* Head Movement Tracking */}
                      <circle cx="900" cy="200" r="60" fill="none" stroke="#ff6b35" strokeWidth="3" strokeDasharray="5,5" />
                      <circle cx="900" cy="200" r="8" fill="#ff6b35" />
                      <line x1="900" y1="200" x2="920" y2="180" stroke="#ff6b35" strokeWidth="3" markerEnd="url(#arrow-head)" />
                    </g>
                    
                    <defs>
                      <marker id="arrow-gaze" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
                        <polygon points="0 0, 10 3, 0 6" fill="#ffeb3b" />
                      </marker>
                      <marker id="arrow-head" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
                        <polygon points="0 0, 10 3, 0 6" fill="#ff6b35" />
                      </marker>
                    </defs>
                  </svg>

                  {/* Gaze Heatmap Legend */}
                  <div className="absolute top-8 left-8 bg-black/95 backdrop-blur-sm border-2 border-[#ff6b35] rounded-xl p-6 max-w-xs">
                    <h4 className="text-lg font-bold text-[#ff6b35] mb-4 flex items-center gap-2" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                      <i className="ri-eye-line"></i>
                      視線ヒートマップ
                    </h4>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#ff6b35]"></div>
                        <span className="text-sm text-white" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>高注視エリア（3.2秒）</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#ffeb3b]"></div>
                        <span className="text-sm text-white" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>中注視エリア（1.5秒）</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#ff6b35] opacity-30"></div>
                        <span className="text-sm text-white" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>低注視エリア（0.3秒）</span>
                      </div>
                    </div>
                  </div>

                  {/* Behavior Metrics */}
                  <div className="absolute top-8 right-8 bg-black/95 backdrop-blur-sm border-2 border-[#ff6b35] rounded-xl p-6 max-w-xs">
                    <h4 className="text-lg font-bold text-[#ff6b35] mb-4" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                      行動メトリクス
                    </h4>
                    <div className="space-y-4">
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-white" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>注視時間</span>
                          <span className="text-lg font-bold text-[#ff6b35]">2.8秒</span>
                        </div>
                        <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden">
                          <div className="h-full w-4/5 bg-[#ff6b35] rounded-full"></div>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-white" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>頭部移動速度</span>
                          <span className="text-lg font-bold text-[#ffeb3b]">45°/s</span>
                        </div>
                        <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden">
                          <div className="h-full w-3/5 bg-[#ffeb3b] rounded-full"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Spatial Recognition Analysis Overlay */}
              {activeAnalysis === 'spatial' && (
                <div className="absolute inset-0 pointer-events-none animate-fadeIn">
                  <svg className="w-full h-full" viewBox="0 0 1200 675">
                    {/* 3D Grid Overlay */}
                    <g opacity="0.4">
                      {[...Array(10)].map((_, i) => (
                        <line key={`v${i}`} x1={i * 120} y1="0" x2={i * 120} y2="675" stroke="#4ade80" strokeWidth="1" />
                      ))}
                      {[...Array(6)].map((_, i) => (
                        <line key={`h${i}`} x1="0" y1={i * 112.5} x2="1200" y2={i * 112.5} stroke="#4ade80" strokeWidth="1" />
                      ))}
                    </g>
                    
                    {/* 3D Position Markers */}
                    <g>
                      {/* Position 1 */}
                      <circle cx="300" cy="350" r="8" fill="#4ade80" />
                      <line x1="300" y1="350" x2="300" y2="550" stroke="#4ade80" strokeWidth="2" strokeDasharray="5,5" />
                      <circle cx="300" cy="550" r="6" fill="#4ade80" opacity="0.5" />
                      <text x="310" y="355" fill="#4ade80" fontSize="14" fontWeight="bold">A (2.5m)</text>
                      
                      {/* Position 2 */}
                      <circle cx="600" cy="300" r="8" fill="#4ade80" />
                      <line x1="600" y1="300" x2="600" y2="550" stroke="#4ade80" strokeWidth="2" strokeDasharray="5,5" />
                      <circle cx="600" cy="550" r="6" fill="#4ade80" opacity="0.5" />
                      <text x="610" y="305" fill="#4ade80" fontSize="14" fontWeight="bold">B (3.2m)</text>
                      
                      {/* Position 3 */}
                      <circle cx="900" cy="400" r="8" fill="#4ade80" />
                      <line x1="900" y1="400" x2="900" y2="550" stroke="#4ade80" strokeWidth="2" strokeDasharray="5,5" />
                      <circle cx="900" cy="550" r="6" fill="#4ade80" opacity="0.5" />
                      <text x="910" y="405" fill="#4ade80" fontSize="14" fontWeight="bold">C (1.8m)</text>
                      
                      {/* Distance Lines */}
                      <line x1="300" y1="350" x2="600" y2="300" stroke="#fbbf24" strokeWidth="3" strokeDasharray="10,5" className="animate-pulse" />
                      <text x="430" y="310" fill="#fbbf24" fontSize="16" fontWeight="bold">3.1m</text>
                      
                      <line x1="600" y1="300" x2="900" y2="400" stroke="#fbbf24" strokeWidth="3" strokeDasharray="10,5" className="animate-pulse" style={{ animationDelay: '0.3s' }} />
                      <text x="730" y="340" fill="#fbbf24" fontSize="16" fontWeight="bold">3.5m</text>
                      
                      {/* Movement Path */}
                      <path 
                        d="M 300 350 Q 450 320 600 300 T 900 400" 
                        stroke="#00d4ff" 
                        strokeWidth="4" 
                        fill="none"
                        strokeDasharray="15,10"
                        markerEnd="url(#arrow-movement)"
                        className="animate-pulse"
                      />
                    </g>
                    
                    <defs>
                      <marker id="arrow-movement" markerWidth="12" markerHeight="12" refX="10" refY="4" orient="auto">
                        <polygon points="0 0, 12 4, 0 8" fill="#00d4ff" />
                      </marker>
                    </defs>
                  </svg>

                  {/* Spatial Metrics */}
                  <div className="absolute top-8 right-8 bg-black/95 backdrop-blur-sm border-2 border-[#4ade80] rounded-xl p-6 max-w-sm">
                    <h4 className="text-lg font-bold text-[#4ade80] mb-4 flex items-center gap-2" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                      <i className="ri-compass-3-line"></i>
                      空間認識データ
                    </h4>
                    <div className="space-y-4">
                      <div className="bg-[#4ade80]/10 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-white" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>移動距離</span>
                          <span className="text-lg font-bold text-[#4ade80]">6.8m</span>
                        </div>
                        <p className="text-xs text-gray-400" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>A → B → C</p>
                      </div>
                      <div className="bg-[#4ade80]/10 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-white" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>平均高さ</span>
                          <span className="text-lg font-bold text-[#4ade80]">2.5m</span>
                        </div>
                        <p className="text-xs text-gray-400" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>床面からの距離</p>
                      </div>
                      <div className="bg-[#4ade80]/10 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-white" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>空間認知精度</span>
                          <span className="text-lg font-bold text-[#fbbf24]">94.2%</span>
                        </div>
                        <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden mt-2">
                          <div className="h-full w-[94%] bg-gradient-to-r from-[#4ade80] to-[#fbbf24] rounded-full"></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 3D Coordinate System */}
                  <div className="absolute bottom-8 left-8 bg-black/95 backdrop-blur-sm border-2 border-[#4ade80] rounded-xl p-4">
                    <div className="flex items-center gap-4">
                      <svg width="80" height="80" viewBox="0 0 80 80">
                        {/* X axis */}
                        <line x1="40" y1="40" x2="70" y2="40" stroke="#ef4444" strokeWidth="2" markerEnd="url(#arrow-x)" />
                        <text x="72" y="44" fill="#ef4444" fontSize="12" fontWeight="bold">X</text>
                        
                        {/* Y axis */}
                        <line x1="40" y1="40" x2="40" y2="10" stroke="#4ade80" strokeWidth="2" markerEnd="url(#arrow-y)" />
                        <text x="36" y="8" fill="#4ade80" fontSize="12" fontWeight="bold">Y</text>
                        
                        {/* Z axis */}
                        <line x1="40" y1="40" x2="15" y2="60" stroke="#00d4ff" strokeWidth="2" markerEnd="url(#arrow-z)" />
                        <text x="8" y="64" fill="#00d4ff" fontSize="12" fontWeight="bold">Z</text>
                        
                        <defs>
                          <marker id="arrow-x" markerWidth="8" markerHeight="8" refX="7" refY="3" orient="auto">
                            <polygon points="0 0, 8 3, 0 6" fill="#ef4444" />
                          </marker>
                          <marker id="arrow-y" markerWidth="8" markerHeight="8" refX="7" refY="3" orient="auto">
                            <polygon points="0 0, 8 3, 0 6" fill="#4ade80" />
                          </marker>
                          <marker id="arrow-z" markerWidth="8" markerHeight="8" refX="7" refY="3" orient="auto">
                            <polygon points="0 0, 8 3, 0 6" fill="#00d4ff" />
                          </marker>
                        </defs>
                      </svg>
                      <div>
                        <p className="text-xs text-gray-400 mb-1" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>3D座標系</p>
                        <p className="text-sm text-white font-mono">X: 2.5m Y: 1.8m Z: 3.2m</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Results Section */}
        <div className="max-w-6xl mx-auto mt-16">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-blue-600/20 to-cyan-600/20 backdrop-blur-sm rounded-2xl p-8 border border-blue-400/30">
              <div className="flex items-start space-x-4">
                <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-speed-up-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>実証された効果</h4>
                  <p className="text-blue-100 mb-4" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>ハーバード医科大学等との共同研究で実証</p>
                  <div className="flex items-center space-x-2">
                    <span className="text-4xl font-bold text-cyan-400">4倍</span>
                    <span className="text-blue-100" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>学習速度向上</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-600/20 to-pink-600/20 backdrop-blur-sm rounded-2xl p-8 border border-purple-400/30">
              <div className="flex items-start space-x-4">
                <div className="w-16 h-16 bg-purple-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>効率化の実現</h4>
                  <p className="text-blue-100 mb-4" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>従来手法と比較して大幅な時間短縮</p>
                  <div className="flex items-center space-x-2">
                    <span className="text-4xl font-bold text-pink-400">75%</span>
                    <span className="text-blue-100" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>時間削減</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Link to VR Impact */}
        <div className="mt-20 text-center">
          <a
            href="https://jollygood.co.jp/vrimpact/ai-strategy"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/50 text-cyan-400 rounded-lg hover:from-cyan-500/30 hover:to-blue-500/30 hover:border-cyan-400 transition-all duration-300 cursor-pointer whitespace-nowrap group"
          >
            <span className="font-bold">VRCHELエンジンの詳細仕様を見る</span>
            <i className="ri-external-link-line text-xl group-hover:translate-x-1 transition-transform"></i>
          </a>
          <p className="text-gray-500 text-sm mt-3">※ 別タブで開きます（VR Impactページへ）</p>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-in-out;
        }
      `}</style>
    </section>
  );
}
